/**
 * useHoursScreen.js
 * -----------------
 * State hook for Education > Lectures > Lecture Hours & Faculty Ledger.
 * Complies strictly with module_implementation_standards.md:
 * - Rule 88: Zero native browser dialogs (appToast / appConfirm).
 * - Rule 89: Asynchronous background export job engine (useExportJob).
 * - Rule 90: Zero mock / demo data in filter dropdowns (fetchLectureHoursMasterOptions).
 * - Enterprise Print Engine: executePrintReport with guardrails.
 */
import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  fetchLectureHours,
  fetchLectureHoursMetrics,
  fetchLectureHoursMasterOptions,
  HOURS_SEARCH_FIELDS,
  HOURS_PREDEFINED_REPORTS,
  ORDER_BY_MAP,
} from '../api/hoursApi'
import { appToast } from '../../../../components/AppToast'
import { useExportJob } from '../../../../hooks/useExportJob'
import { executePrintReport } from '../../../../utils/printReportUtil'

export function generateAcademicYears(startYear = 2020, endYear = 2035) {
  const years = [{ value: 'All', label: 'All Academic Years' }]
  for (let y = endYear; y >= startYear; y--) {
    const nextY = String(y + 1).slice(-2)
    const val = `${y}-${nextY}`
    years.push({ value: val, label: val })
  }
  return years
}

const ALL_ACADEMIC_YEARS = generateAcademicYears()

function computeDateRangeForPeriod(periodVal) {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()
  const d = now.getDate()

  const fmt = (dt) => {
    const pad = (n) => String(n).padStart(2, '0')
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`
  }

  switch (String(periodVal)) {
    case '1': // Current Year
      return { from: fmt(new Date(y, 0, 1)), to: fmt(new Date(y, 11, 31)) }
    case '2': // Current Month
      return { from: fmt(new Date(y, m, 1)), to: fmt(new Date(y, m + 1, 0)) }
    case '3': { // Current Week (starting Monday)
      const day = now.getDay() || 7
      const monday = new Date(now)
      monday.setDate(d - day + 1)
      const sunday = new Date(monday)
      sunday.setDate(monday.getDate() + 6)
      return { from: fmt(monday), to: fmt(sunday) }
    }
    case '4': // Last Year
      return { from: fmt(new Date(y - 1, 0, 1)), to: fmt(new Date(y - 1, 11, 31)) }
    case '5': // Last Month
      return { from: fmt(new Date(y, m - 1, 1)), to: fmt(new Date(y, m, 0)) }
    case '6': { // Last Week
      const day = now.getDay() || 7
      const lastMon = new Date(now)
      lastMon.setDate(d - day - 6)
      const lastSun = new Date(lastMon)
      lastSun.setDate(lastMon.getDate() + 6)
      return { from: fmt(lastMon), to: fmt(lastSun) }
    }
    case '12': // Today
      return { from: fmt(now), to: fmt(now) }
    case '13': { // Yesterday
      const yest = new Date(now)
      yest.setDate(d - 1)
      return { from: fmt(yest), to: fmt(yest) }
    }
    default:
      return null
  }
}

export default function useHoursScreen() {
  const [loading, setLoading] = useState(false)
  const [records, setRecords] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [orderBy, setOrderBy] = useState('date') // default 7
  const [sortOrder, setSortOrder] = useState('desc')
  const [isReportsOpen, setIsReportsOpen] = useState(false)

  // Filters State
  const [searchText, setSearchText] = useState('')
  const [searchField, setSearchField] = useState('All')
  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [courseFilter, setCourseFilter] = useState('All')
  const [schoolFilter, setSchoolFilter] = useState('All')
  const [facultyFilter, setFacultyFilter] = useState('All')
  const [academicYearFilter, setAcademicYearFilter] = useState('2026-27')
  const [periodFilter, setPeriodFilter] = useState('Period')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [batchFilter, setBatchFilter] = useState('All')
  const [subjectFilter, setSubjectFilter] = useState('All')
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // Metrics State
  const [metrics, setMetrics] = useState({
    totalRecords: 0,
    totalMinutes: 0,
    totalHoursString: '0h 0m',
    hoursFormattedSummary: '0 lectures (0h 0m)',
    uniqueFacultyCount: 0,
    uniqueCourseCount: 0,
    avgDurationMinutes: 0,
  })

  // Dynamic Master Options (Rule 90)
  const [masterOptions, setMasterOptions] = useState({
    companies: [{ value: 'All', label: 'All Companies' }],
    branches: [{ value: 'All', label: 'All Branches' }],
    courses: [{ value: 'All', label: 'All Courses' }],
    batches: [{ value: 'All', label: 'All Batches' }],
    subjects: [{ value: 'All', label: 'All Subjects' }],
    faculty: [{ value: 'All', label: 'All Faculty' }],
    schools: [{ value: 'All', label: 'All Schools / Colleges' }],
  })

  // Load real master options on mount
  useEffect(() => {
    async function loadOptions() {
      try {
        const res = await fetchLectureHoursMasterOptions()
        setMasterOptions({
          companies: [{ value: 'All', label: 'All Companies' }, ...res.companies],
          branches: [{ value: 'All', label: 'All Branches' }, ...res.branches],
          courses: [{ value: 'All', label: 'All Courses' }, ...res.courses],
          batches: [{ value: 'All', label: 'All Batches' }, ...res.batches],
          subjects: [{ value: 'All', label: 'All Subjects' }, ...res.subjects],
          faculty: [{ value: 'All', label: 'All Faculty' }, ...res.faculty],
          schools: [{ value: 'All', label: 'All Schools / Colleges' }, ...res.schools],
        })
      } catch (err) {
        console.error('Failed to load master options for lecture hours:', err)
      }
    }
    loadOptions()
  }, [])

  // Period Preset handler
  const handlePeriodChange = useCallback((val) => {
    setPeriodFilter(val)
    const range = computeDateRangeForPeriod(val)
    if (range) {
      setFromDate(range.from)
      setToDate(range.to)
      setCurrentPage(1)
    }
  }, [])

  // Background Export Job Engine (Rule 89)
  const {
    triggerExport,
    isExporting,
  } = useExportJob({
    jobType: 'EXPORT',
    module: 'lecture_hours',
  })

  // Load Data & Metrics from Live Backend
  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const orderNum = typeof orderBy === 'number' ? orderBy : ORDER_BY_MAP[orderBy] || 7

      const filterPayload = {
        searchText,
        searchField,
        companyFilter,
        branchFilter,
        courseFilter,
        schoolFilter,
        facultyFilter,
        academicYear: academicYearFilter,
        fromDate,
        toDate,
        batchFilter,
        subjectFilter,
        orderBy: orderNum,
        page: currentPage,
        pageSize,
      }

      const [res, metricsRes] = await Promise.all([
        fetchLectureHours(filterPayload),
        fetchLectureHoursMetrics(filterPayload),
      ])

      setRecords(res.records)
      setTotalCount(res.totalCount)
      setTotalPages(res.totalPages)
      setMetrics(metricsRes)
    } catch (err) {
      console.error('Error fetching lecture hours:', err)
      appToast.error('Failed to load lecture hours data.')
    } finally {
      setLoading(false)
    }
  }, [
    searchText,
    searchField,
    companyFilter,
    branchFilter,
    courseFilter,
    schoolFilter,
    facultyFilter,
    academicYearFilter,
    fromDate,
    toDate,
    batchFilter,
    subjectFilter,
    orderBy,
    currentPage,
    pageSize,
    refreshTrigger,
  ])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Sorting
  const handleSort = (field) => {
    if (orderBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setOrderBy(field)
      setSortOrder('asc')
    }
    setCurrentPage(1)
  }

  // Reset Filters
  const handleReset = () => {
    setSearchText('')
    setSearchField('All')
    setCompanyFilter('All')
    setBranchFilter('All')
    setCourseFilter('All')
    setSchoolFilter('All')
    setFacultyFilter('All')
    setAcademicYearFilter('2026-27')
    setPeriodFilter('Period')
    setFromDate('')
    setToDate('')
    setBatchFilter('All')
    setSubjectFilter('All')
    setOrderBy('date')
    setCurrentPage(1)
    appToast.info('Filters have been reset.')
  }

  // Background Excel Export (Rule 89)
  const handleExportExcel = useCallback(async () => {
    const orderNum = typeof orderBy === 'number' ? orderBy : ORDER_BY_MAP[orderBy] || 7
    try {
      await triggerExport(
        {
          companyId: companyFilter !== 'All' ? companyFilter : undefined,
          branchId: branchFilter !== 'All' ? branchFilter : undefined,
          courseId: courseFilter !== 'All' ? courseFilter : undefined,
          batchId: batchFilter !== 'All' ? batchFilter : undefined,
          subjectId: subjectFilter !== 'All' ? subjectFilter : undefined,
          facultyId: facultyFilter !== 'All' ? facultyFilter : undefined,
          schoolId: schoolFilter !== 'All' ? schoolFilter : undefined,
          academicYear: academicYearFilter !== 'All' ? academicYearFilter : undefined,
          fromDate,
          toDate,
          searchField,
          searchText,
          orderBy: orderNum,
        },
        'XLSX',
      )
    } catch (err) {
      console.error('Failed to start Excel export:', err)
      appToast.error('Failed to initiate export job.')
    }
  }, [
    triggerExport,
    orderBy,
    companyFilter,
    branchFilter,
    courseFilter,
    batchFilter,
    subjectFilter,
    facultyFilter,
    schoolFilter,
    academicYearFilter,
    fromDate,
    toDate,
    searchField,
    searchText,
  ])

  // Background PDF Export (Rule 89)
  const handleExportPdf = useCallback(async () => {
    const orderNum = typeof orderBy === 'number' ? orderBy : ORDER_BY_MAP[orderBy] || 7
    try {
      await triggerExport(
        {
          companyId: companyFilter !== 'All' ? companyFilter : undefined,
          branchId: branchFilter !== 'All' ? branchFilter : undefined,
          courseId: courseFilter !== 'All' ? courseFilter : undefined,
          batchId: batchFilter !== 'All' ? batchFilter : undefined,
          subjectId: subjectFilter !== 'All' ? subjectFilter : undefined,
          facultyId: facultyFilter !== 'All' ? facultyFilter : undefined,
          schoolId: schoolFilter !== 'All' ? schoolFilter : undefined,
          academicYear: academicYearFilter !== 'All' ? academicYearFilter : undefined,
          fromDate,
          toDate,
          searchField,
          searchText,
          orderBy: orderNum,
        },
        'PDF',
      )
    } catch (err) {
      console.error('Failed to start PDF export:', err)
      appToast.error('Failed to initiate export job.')
    }
  }, [
    triggerExport,
    orderBy,
    companyFilter,
    branchFilter,
    courseFilter,
    batchFilter,
    subjectFilter,
    facultyFilter,
    schoolFilter,
    academicYearFilter,
    fromDate,
    toDate,
    searchField,
    searchText,
  ])

  // Print Report (Report 5300: Lecture Hours Report with 1,000-row guardrail)
  const handlePrintReport = useCallback(
    async (reportConfig) => {
      const orderNum = typeof orderBy === 'number' ? orderBy : ORDER_BY_MAP[orderBy] || 7
      const queryParams = {
        academicYear: academicYearFilter,
        companyFilter,
        branchFilter,
        courseFilter,
        schoolFilter,
        facultyFilter,
        fromDate,
        toDate,
        batchFilter,
        subjectFilter,
        orderBy: orderNum,
        searchField,
        searchText,
      }

      await executePrintReport({
        title: 'Lecture Hours & Faculty Teaching Report',
        subtitle: `Academic Year: ${academicYearFilter === 'All' ? 'All Years' : academicYearFilter} | Total Hours: ${metrics.totalHoursString}`,
        metadata: {
          'Academic Year': academicYearFilter,
          'Faculty': facultyFilter === 'All' ? 'All Faculty' : (masterOptions.faculty.find((f) => f.value === facultyFilter)?.label || facultyFilter),
          'Course': courseFilter === 'All' ? 'All Courses' : (masterOptions.courses.find((c) => c.value === courseFilter)?.label || courseFilter),
          'Period': periodFilter !== 'Period' ? periodFilter : (fromDate && toDate ? `${fromDate} to ${toDate}` : 'All Dates'),
        },
        columns: [
          { key: 'faculty', label: 'Faculty' },
          { key: 'date', label: 'Date' },
          { key: 'time_slot', label: 'Time Slot', format: (val, row) => `${row.startTime || ''} - ${row.endTime || ''}` },
          { key: 'timeDiff', label: 'Duration' },
          { key: 'course', label: 'Course' },
          { key: 'subject', label: 'Subject' },
          {
            key: 'chapterName',
            label: 'Topic / Chapter',
            format: (val, row) => (row.chapterName ? `${row.chapterNo ? 'Ch.' + row.chapterNo + ': ' : ''}${row.chapterName}` : '—'),
          },
          { key: 'branch', label: 'Branch' },
          { key: 'batch', label: 'Batch' },
          { key: 'company', label: 'Company' },
        ],
        fetchReportData: async () => {
          const res = await fetchLectureHours({
            ...queryParams,
            page: 1,
            pageSize: 1000,
          })
          return res?.records || []
        },
        onExportPdf: handleExportPdf,
        onExportExcel: handleExportExcel,
      })
    },
    [
      academicYearFilter,
      companyFilter,
      branchFilter,
      courseFilter,
      schoolFilter,
      facultyFilter,
      fromDate,
      toDate,
      batchFilter,
      subjectFilter,
      orderBy,
      searchField,
      searchText,
      metrics.totalHoursString,
      periodFilter,
      masterOptions,
      handleExportPdf,
      handleExportExcel,
    ],
  )

  const handleReportAction = useCallback(
    async (actionKey, reportKey) => {
      if (actionKey === 'print') {
        await handlePrintReport({ reportKey })
      } else if (actionKey === 'excel') {
        handleExportExcel()
      } else if (actionKey === 'pdf') {
        handleExportPdf()
      }
    },
    [handlePrintReport, handleExportExcel, handleExportPdf],
  )

  const options = useMemo(() => {
    return {
      searchFields: HOURS_SEARCH_FIELDS,
      companies: masterOptions.companies,
      branches: masterOptions.branches,
      courses: masterOptions.courses,
      schools: masterOptions.schools,
      faculty: masterOptions.faculty,
      batches: masterOptions.batches,
      subjects: masterOptions.subjects,
      academicYears: ALL_ACADEMIC_YEARS,
    }
  }, [masterOptions])

  return {
    state: {
      loading,
      records,
      totalCount,
      totalPages,
      currentPage,
      pageSize,
      sortBy: orderBy,
      sortOrder,
      metrics,
      isReportsOpen,
      isExporting,
      // Filter values
      searchText,
      searchField,
      companyFilter,
      branchFilter,
      courseFilter,
      schoolFilter,
      facultyFilter,
      academicYearFilter,
      periodFilter,
      fromDate,
      toDate,
      batchFilter,
      subjectFilter,
      options,
      predefinedReports: HOURS_PREDEFINED_REPORTS,
    },
    actions: {
      setCurrentPage,
      setPageSize,
      handleSort,
      handleReset,
      setIsReportsOpen,
      handleExportExcel,
      handleExportPdf,
      handlePrintReport,
      handleReportAction,
      setSearchText,
      setSearchField,
      setCompanyFilter,
      setBranchFilter,
      setCourseFilter,
      setSchoolFilter,
      setFacultyFilter,
      setAcademicYearFilter,
      setPeriodFilter: handlePeriodChange,
      setFromDate,
      setToDate,
      setBatchFilter,
      setSubjectFilter,
      reload: () => setRefreshTrigger((p) => p + 1),
    },
  }
}
